import clsx from "clsx";
import { PropsWithChildren, useCallback, useEffect, useRef } from "react";

interface HdOffcanvasProps {
  isOpen: boolean;
  position: "left" | "right";
  width?: string;
  className?: string;
  closeOnClickOutside?: boolean;
  closeOnEsc?: boolean;
  allowScroll?: boolean;
  onClose?: () => void;
  backdrop?: boolean;
}

function HdOffcanvas({
  className,
  isOpen,
  position,
  width,
  closeOnClickOutside = true,
  closeOnEsc = true,
  allowScroll = false,
  onClose,
  backdrop,
  children,
}: PropsWithChildren<HdOffcanvasProps>) {
  const handleClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const ref = useRef<HTMLDivElement>(null);

  // occhio: se il DOM cambia tra pointerdown e pointerup (es. popover react-aria
  // che si apre al press) il browser ritargetta il click sul body, quindi il
  // solo stopPropagation non basta. Mi tengo il target del pointerdown e decido su quello.
  const pressTargetRef = useRef<EventTarget | null>(null);

  const onPointerDown = useCallback((event: PointerEvent) => {
    pressTargetRef.current = event.target;
  }, []);

  const onClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = (pressTargetRef.current ?? event.target) as Node | null;
      pressTargetRef.current = null;

      if (!closeOnClickOutside || !isOpen) return;
      if (!target || !target.isConnected) return;
      if (ref.current?.contains(target)) return;

      // portals fuori da #root (popover, menu, ecc.) non è un click fuori, 
      // questo causava la chiusura del canvas clickando ad esempio su una dropdown al suo interno in react-aria
      const appRoot = document.getElementById("root");
      if (appRoot && !appRoot.contains(target)) return;

      if (handleClose) handleClose();
    },
    [closeOnClickOutside, isOpen, handleClose]
  );

  const onEscKey = useCallback(
    (event: KeyboardEvent) => {
      if (!closeOnEsc) return;

      if (event.key === "Escape") {
        if (isOpen) {
          if (handleClose) handleClose();
        }
      }
    },
    [closeOnEsc, isOpen, handleClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", onEscKey, false);
    return () => document.removeEventListener("keydown", onEscKey);
  }, [onEscKey]);

  useEffect(() => {
    // pointerdown in capture, così lo becco anche se qualcuno stoppa la propagazione
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("click", onClickOutside, false);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("click", onClickOutside);
    };
  }, [onPointerDown, onClickOutside]);

  // useEffect(() => {
  //   if (top) {
  //     top.addEventListener("blur", onClickOutside, false);
  //     return () =>
  //       top ? top.removeEventListener("blur", onClickOutside) : undefined;
  //   }
  // }, [onClickOutside]);

  useEffect(() => {
    if (!allowScroll) {
      if (isOpen) document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, allowScroll]);

  useEffect(() => {
    if (isOpen) {
      ref.current?.focus();
    }
  }, [isOpen]);

  return (
    <>
      {backdrop && (
        <div
          className={clsx(
            isOpen ? "visible opacity-100" : "invisible opacity-0",
            "fixed top-0 left-0 w-full h-full z-40 bg-[#00000030] transition-[visibility] duration-200 ease-in-out "
          )}
        />
      )}
      <div
        tabIndex={0}
        ref={ref}
        role="dialog"
        style={{ width: width }}
        aria-modal="true"
        onClick={(ev) => ev.stopPropagation()}
        className={clsx(
          "fixed bottom-0 top-0 z-40 outline-none transition transform duration-300 ease-in-out ",
          position === "left" ? "left-0" : "right-0",
          isOpen
            ? "translate-x-0"
            : position === "left"
            ? "translate-x-[-100%]"
            : "translate-x-[100%]",
          className
        )}
      >
        {isOpen && children}
      </div>
    </>
  );
}

export default HdOffcanvas;
